import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  useState,
} from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ActionButton } from "../../components/Buttons";
import { auth } from "../../configs/firebaseConfig";
import { useAuth } from "../../contexts/AuthContext";
import {
  getAnalyticsUserState,
  trackEvent,
  trackMixpanelEvent,
} from "../../utils/Analytics";
import {
  getPublicActiveWallById,
  getPublicPartnerById,
  getPublicWallCards,
} from "../../utils/firestore";
import {
  DEFAULT_PUBLIC_WALL,
  DEFAULT_WALL_ID,
} from "../../utils/cards/cardConstants";
import { normalizePublicWallCard } from "../../utils/cards/normalizeWallCard";
import BackgroundLights from "./components/BackgroundLights";
import EmpathyPageHeader from "./components/EmpathyPageHeader";
import ShareCardPreviewView from "./components/ShareCardPreviewView";
import StaticWallCards from "./components/StaticWallCards";
import WallCardDetailModal from "./components/WallCardDetailModal";
import usePreparedCardShare from "./usePreparedCardShare";
import { buildWallDisplayCards } from "./wallFallbackCards";

const WALL_CARD_PAGE_SIZE = 8;
const PARTNER_LOGO_LOAD_TIMEOUT_MS = 1500;
const SHOW_FALLBACK_WALL_CARDS = false;

const REFINE_DARK_BACKGROUND = "#050604";
const WALL_HELPER_COPY = "Tap a card to open.";

function getCreateCardPath(wall) {
  const wallId =
    typeof wall?.id === "string" && wall.id.trim()
      ? wall.id.trim()
      : "";

  return wallId ? `/refine?wallId=${encodeURIComponent(wallId)}` : "";
}

function isPermissionDeniedError(error) {
  return error?.code === "permission-denied";
}

function isInvalidWallCursorError(error) {
  return error?.code === "invalid-request" &&
    error?.status === 400 &&
    error?.message === "Invalid cursor.";
}

function getWallPartnerId(wall) {
  const partnerId =
    typeof wall?.partnerId === "string" ? wall.partnerId.trim() : "";

  return partnerId && !partnerId.includes("/") ? partnerId : "";
}

function waitForPartnerLogo(logoUrl) {
  if (
    typeof logoUrl !== "string" ||
    (!logoUrl.startsWith("/") && !/^https?:\/\//i.test(logoUrl))
  ) {
    return Promise.resolve(false);
  }

  return new Promise((resolve) => {
    const image = new Image();
    let settled = false;
    let timeoutId;
    const finish = (loaded) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timeoutId);
      image.onload = null;
      image.onerror = null;
      resolve(loaded);
    };

    timeoutId = setTimeout(
      () => finish(false),
      PARTNER_LOGO_LOAD_TIMEOUT_MS
    );
    image.onload = () => finish(true);
    image.onerror = () => finish(false);
    image.src = logoUrl;
    if (image.complete && image.naturalWidth > 0) {
      finish(true);
    }
  });
}

function normalizeWallCardPage(result) {
  if (Array.isArray(result)) {
    return { seedCards: [], cards: result, nextCursor: null };
  }

  return {
    seedCards: Array.isArray(result?.seedCards) ? result.seedCards : [],
    cards: Array.isArray(result?.cards) ? result.cards : [],
    nextCursor: typeof result?.nextCursor === "string" && result.nextCursor
      ? result.nextCursor
      : null,
  };
}

function WallCardInteractionLayer({
  cards,
  emptyMessage,
  header,
  wallTitle,
  createCardPath,
  isLoading,
  hasMore,
  isLoadingMore,
  loadMoreError,
  onLoadMore,
  onRetryLoadMore,
  arrivalCardId,
  highlightedCardId,
  onArrivalTargetRendered,
  partnerId,
  seedCardIds,
  communityCardIds,
  wallId,
  scrollResetKey,
}) {
  const [selectedCardIndex, setSelectedCardIndex] = useState(null);
  const {
    closeSharePreview,
    copyShareLink,
    openShareSheet,
    prepareShare,
    resetShareState,
    shareError,
    sharePreview,
    sharing,
    updateSharePreviewMessage,
  } = usePreparedCardShare({
    missingCardMessage:
      "We couldn't find the public card link. Please try another card.",
    source: "Wall",
  });
  const pendingNextCardIndexRef = useRef(null);
  const selectedCard =
    typeof selectedCardIndex === "number" ? cards[selectedCardIndex] : null;

  useEffect(() => {
    const pendingIndex = pendingNextCardIndexRef.current;

    if (pendingIndex === null) {
      return;
    }

    if (cards.length > pendingIndex + 1) {
      pendingNextCardIndexRef.current = null;
      setSelectedCardIndex(pendingIndex + 1);
    } else if (!isLoadingMore) {
      pendingNextCardIndexRef.current = null;
    }
  }, [cards.length, isLoadingMore]);

  const openCardDetail = useCallback((cardIndex) => {
    pendingNextCardIndexRef.current = null;
    resetShareState();
    const card = cards[cardIndex];
    if (card?.id) {
      const cardSource = seedCardIds.has(card.id)
        ? "seed"
        : communityCardIds.has(card.id)
          ? "community"
          : "";
      trackMixpanelEvent("Wall Card Opened", {
        card_id: card.id,
        wall_id: wallId,
        partner_id: partnerId,
        ...(cardSource ? { card_source: cardSource } : {}),
      });
    }
    setSelectedCardIndex(cardIndex);
  }, [cards, communityCardIds, partnerId, resetShareState, seedCardIds, wallId]);

  const closeCardDetail = () => {
    pendingNextCardIndexRef.current = null;
    resetShareState();
    setSelectedCardIndex(null);
  };

  const showPreviousCard = () => {
    if (typeof selectedCardIndex !== "number" || selectedCardIndex <= 0) {
      return;
    }

    pendingNextCardIndexRef.current = null;
    resetShareState();
    setSelectedCardIndex(selectedCardIndex - 1);
  };

  const showNextCard = () => {
    if (typeof selectedCardIndex !== "number" || isLoadingMore) {
      return;
    }

    if (selectedCardIndex < cards.length - 1) {
      pendingNextCardIndexRef.current = null;
      resetShareState();
      setSelectedCardIndex(selectedCardIndex + 1);
      return;
    }

    if (!hasMore) {
      return;
    }

    resetShareState();
    pendingNextCardIndexRef.current = selectedCardIndex;
    if (loadMoreError) {
      onRetryLoadMore?.();
    } else {
      onLoadMore?.();
    }
  };

  const shareCard = async (card) => {
    trackEvent("Engagement", "Shared wall card", card.theme);

    await prepareShare({
      cardId: card?.id,
      question: card?.question,
    });
  };

  const trackCreateCardFromModal = (card) => {
    trackEvent("CTA", "Create Card From Wall Card", card.theme);
  };

  return (
    <>
      <StaticWallCards
        cards={cards}
        header={isLoading ? null : header}
        isLoading={isLoading}
        emptyMessage={emptyMessage}
        onCardClick={openCardDetail}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        loadMoreError={loadMoreError}
        onLoadMore={onLoadMore}
        onRetryLoadMore={onRetryLoadMore}
        arrivalCardId={arrivalCardId}
        highlightedCardId={highlightedCardId}
        onArrivalTargetRendered={onArrivalTargetRendered}
        wallId={wallId}
        scrollResetKey={scrollResetKey}
      />

      <WallCardDetailModal
        card={selectedCard}
        wallTitle={wallTitle}
        showNavigation={cards.length > 1 || hasMore}
        canNavigatePrevious={selectedCardIndex > 0}
        canNavigateNext={
          typeof selectedCardIndex === "number" &&
          !isLoadingMore &&
          (selectedCardIndex < cards.length - 1 || hasMore)
        }
        nextNavigationLabel={loadMoreError ? "Retry loading next card" : "Next card"}
        onClose={closeCardDetail}
        onPreviousCard={showPreviousCard}
        onNextCard={showNextCard}
        onCreateCard={trackCreateCardFromModal}
        onShare={shareCard}
        shareError={sharePreview ? "" : shareError}
        sharing={sharing}
        createCardPath={createCardPath}
      />

      {sharePreview ? (
        <ShareCardPreviewView
          selectedIntroId={sharePreview.selectedIntroId}
          cardUrl={sharePreview.cardUrl}
          isPreparing={sharePreview.isPreparing}
          preparationFailed={sharePreview.preparationFailed}
          shareError={shareError}
          onMessageChange={updateSharePreviewMessage}
          onOpenShareSheet={openShareSheet}
          onPrepareShareLink={() => shareCard(selectedCard)}
          onCopyLink={copyShareLink}
          onClose={closeSharePreview}
        />
      ) : null}
    </>
  );
}

const WallView = () => {
  const { wallId: wallIdParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [liveCards, setLiveCards] = useState([]);
  const [seedCards, setSeedCards] = useState([]);
  const [wall, setWall] = useState(null);
  const [wallUnavailable, setWallUnavailable] = useState(false);
  const [cardsLoadFailed, setCardsLoadFailed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [paginationCursor, setPaginationCursor] = useState(null);
  const [hasMoreCards, setHasMoreCards] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [paginationLoadFailed, setPaginationLoadFailed] = useState(false);
  const [arrival, setArrival] = useState(null);
  const [arrivalSlow, setArrivalSlow] = useState(false);
  const [arrivalFallback, setArrivalFallback] = useState(false);
  const [arrivalToastVisible, setArrivalToastVisible] = useState(false);
  const [highlightedCardId, setHighlightedCardId] = useState("");
  const wallSessionRef = useRef(0);
  const paginationRequestKeyRef = useRef(null);
  const arrivalAdditionalPagesRef = useRef(0);
  const cursorRecoveryAttemptedRef = useRef(false);
  const incomingNavigationWallIdRef = useRef("");
  const trackedWallVisitRef = useRef("");
  const arrivalToastTimerRef = useRef(null);
  const arrivalHighlightTimerRef = useRef(null);
  const previousLocationKeyRef = useRef(location.key);
  const suppressNextLocationResetRef = useRef(false);
  const activeUser = auth.currentUser || currentUser;
  const wallId =
    typeof wallIdParam === "string" && wallIdParam.trim()
      ? wallIdParam.trim()
      : DEFAULT_WALL_ID;
  const previousWallIdRef = useRef(wallId);

  const clearArrivalFeedback = useCallback(() => {
    setArrival(null);
    setArrivalSlow(false);
    setArrivalFallback(false);
    setArrivalToastVisible(false);
    setHighlightedCardId("");
    arrivalAdditionalPagesRef.current = 0;
    cursorRecoveryAttemptedRef.current = false;
    clearTimeout(arrivalToastTimerRef.current);
    clearTimeout(arrivalHighlightTimerRef.current);
  }, []);

  useEffect(() => {
    const candidate = location.state;
    const addedCardId =
      typeof candidate?.addedCardId === "string"
        ? candidate.addedCardId.trim()
        : "";
    const submittedWallId =
      typeof candidate?.wallId === "string" ? candidate.wallId.trim() : "";

    if (!addedCardId || !submittedWallId) {
      incomingNavigationWallIdRef.current = "";
      if (previousLocationKeyRef.current !== location.key) {
        previousLocationKeyRef.current = location.key;
        if (suppressNextLocationResetRef.current) {
          suppressNextLocationResetRef.current = false;
        } else {
          clearArrivalFeedback();
        }
      }
      return;
    }

    previousLocationKeyRef.current = location.key;
    suppressNextLocationResetRef.current = true;
    incomingNavigationWallIdRef.current =
      submittedWallId === wallId && !addedCardId.includes("/")
        ? submittedWallId
        : "";
    clearArrivalFeedback();
    setArrival(
      submittedWallId === wallId && !addedCardId.includes("/")
        ? { cardId: addedCardId, wallId: submittedWallId }
        : null
    );
    setArrivalSlow(false);
    setArrivalFallback(false);
    setArrivalToastVisible(false);
    setHighlightedCardId("");
    arrivalAdditionalPagesRef.current = 0;
    clearTimeout(arrivalToastTimerRef.current);
    clearTimeout(arrivalHighlightTimerRef.current);

    // Replacing this history entry prevents refresh and forward navigation from
    // replaying the submission arrival experience.
    navigate(`${location.pathname}${location.search}${location.hash}`, {
      replace: true,
      state: null,
    });
  }, [clearArrivalFeedback, location, navigate, wallId]);

  useEffect(() => {
    if (!arrival || arrival.wallId !== wallId) {
      return undefined;
    }

    const timer = setTimeout(() => setArrivalSlow(true), 4000);
    return () => clearTimeout(timer);
  }, [arrival, wallId]);

  const handleArrivalTargetRendered = useCallback((cardId) => {
    setArrival(null);
    setArrivalToastVisible(true);
    setHighlightedCardId(cardId);
    setArrivalSlow(false);
    setArrivalFallback(false);
    clearTimeout(arrivalToastTimerRef.current);
    clearTimeout(arrivalHighlightTimerRef.current);
    arrivalToastTimerRef.current = setTimeout(
      () => setArrivalToastVisible(false),
      7000
    );
    arrivalHighlightTimerRef.current = setTimeout(
      () => setHighlightedCardId(""),
      4000
    );
  }, []);

  useEffect(() => () => {
    clearTimeout(arrivalToastTimerRef.current);
    clearTimeout(arrivalHighlightTimerRef.current);
  }, []);

  const applyWallCardPage = useCallback((result, requestedWallId) => {
    const page = normalizeWallCardPage(result);
    const normalizedCards = page.cards
      .map((card) => normalizePublicWallCard(card, requestedWallId))
      .filter(Boolean);

    const normalizedSeedCards = page.seedCards
      .map((card) => normalizePublicWallCard(card, requestedWallId))
      .filter(Boolean);

    return {
      ...page,
      seedCards: normalizedSeedCards,
      cards: normalizedCards,
    };
  }, []);

  useLayoutEffect(() => {
    const rootElement = document.getElementById("root");

    let themeColorMeta = document.querySelector(
      'meta[name="theme-color"]'
    );

    let statusBarMeta = document.querySelector(
      'meta[name="apple-mobile-web-app-status-bar-style"]'
    );

    const createdThemeColorMeta = !themeColorMeta;
    const createdStatusBarMeta = !statusBarMeta;

    const previousHtmlBackground =
      document.documentElement.style.backgroundColor;
    const previousBodyBackground =
      document.body.style.backgroundColor;
    const previousRootBackground =
      rootElement?.style.backgroundColor || "";
    const previousThemeColor =
      themeColorMeta?.getAttribute("content") || "";
    const previousStatusBarStyle =
      statusBarMeta?.getAttribute("content") || "";

    if (!themeColorMeta) {
      themeColorMeta = document.createElement("meta");
      themeColorMeta.setAttribute("name", "theme-color");
      document.head.appendChild(themeColorMeta);
    }

    if (!statusBarMeta) {
      statusBarMeta = document.createElement("meta");
      statusBarMeta.setAttribute(
        "name",
        "apple-mobile-web-app-status-bar-style"
      );
      document.head.appendChild(statusBarMeta);
    }

    document.documentElement.style.backgroundColor =
      REFINE_DARK_BACKGROUND;
    document.body.style.backgroundColor =
      REFINE_DARK_BACKGROUND;

    themeColorMeta.setAttribute(
      "content",
      REFINE_DARK_BACKGROUND
    );

    statusBarMeta.setAttribute("content", "black");

    if (rootElement) {
      rootElement.style.backgroundColor =
        REFINE_DARK_BACKGROUND;
    }

    return () => {
      document.documentElement.style.backgroundColor =
        previousHtmlBackground;

      document.body.style.backgroundColor =
        previousBodyBackground;

      if (rootElement) {
        rootElement.style.backgroundColor =
          previousRootBackground;
      }

      if (createdThemeColorMeta) {
        themeColorMeta.remove();
      } else {
        themeColorMeta.setAttribute(
          "content",
          previousThemeColor
        );
      }

      if (createdStatusBarMeta) {
        statusBarMeta.remove();
      } else {
        statusBarMeta.setAttribute(
          "content",
          previousStatusBarStyle
        );
      }
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const wallRouteChanged = previousWallIdRef.current !== wallId;
    if (wallRouteChanged) {
      previousWallIdRef.current = wallId;
      const hasMatchingArrival = incomingNavigationWallIdRef.current === wallId;
      if (!hasMatchingArrival) {
        setArrival(null);
      }
      setArrivalSlow(false);
      setArrivalFallback(false);
      setArrivalToastVisible(false);
      setHighlightedCardId("");
      arrivalAdditionalPagesRef.current = 0;
      cursorRecoveryAttemptedRef.current = false;
      clearTimeout(arrivalToastTimerRef.current);
      clearTimeout(arrivalHighlightTimerRef.current);
    }
    const sessionId = wallSessionRef.current + 1;
    wallSessionRef.current = sessionId;
    paginationRequestKeyRef.current = null;

    // A wall route can change without remounting this view. Clear the previous
    // wall's cards before loading the destination wall.
    setLiveCards([]);
    setSeedCards([]);
    setWall(null);
    setWallUnavailable(false);
    setCardsLoadFailed(false);
    setIsLoading(true);
    setPaginationCursor(null);
    setHasMoreCards(false);
    setIsLoadingMore(false);
    setPaginationLoadFailed(false);

    const setLoadedWall = async (wallDocument) => {
      setWall({ ...wallDocument, partnerBranding: null });
      setWallUnavailable(false);

      const partnerId = getWallPartnerId(wallDocument);
      if (!partnerId) {
        return;
      }

      let partner = await getPublicPartnerById(partnerId).catch((error) => {
          console.error("Unable to load optional wall partner branding", error);
          return null;
        });

      if (!isMounted || wallSessionRef.current !== sessionId) {
        return;
      }

      if (partner?.logoUrl) {
        const logoLoaded = await waitForPartnerLogo(partner.logoUrl);
        if (!isMounted || wallSessionRef.current !== sessionId) {
          return;
        }
        if (!logoLoaded) {
          partner = { ...partner, logoUrl: "" };
        }
      }

      setWall((currentWall) =>
        currentWall
          ? { ...currentWall, partnerBranding: partner }
          : currentWall
      );
    };

    const loadWall = async () => {
      let partnerBrandingPromise = Promise.resolve();
      try {
        const wallDocument = await getPublicActiveWallById(wallId);

        if (!isMounted) {
          return;
        }

        if (!wallDocument) {
          setWall(null);
          setLiveCards([]);
          setWallUnavailable(true);
          setCardsLoadFailed(false);
          return;
        }

        partnerBrandingPromise = setLoadedWall(wallDocument);

        const page = applyWallCardPage(
          await getPublicWallCards(wallId, WALL_CARD_PAGE_SIZE),
          wallId
        );

        if (!isMounted) {
          return;
        }

        setCardsLoadFailed(false);
        setSeedCards(page.seedCards);
        setLiveCards(page.cards);
        setPaginationCursor(page.nextCursor);
        setHasMoreCards(Boolean(page.nextCursor));
      } catch (error) {
        if (!isMounted) {
          return;
        }

        console.error("Unable to load public wall", error);

        if (wallId === DEFAULT_WALL_ID && isPermissionDeniedError(error)) {
          partnerBrandingPromise = setLoadedWall(DEFAULT_PUBLIC_WALL);

          try {
            const page = applyWallCardPage(
              await getPublicWallCards(wallId, WALL_CARD_PAGE_SIZE),
              wallId
            );

            if (!isMounted) {
              return;
            }

            setCardsLoadFailed(false);
            setSeedCards(page.seedCards);
            setLiveCards(page.cards);
            setPaginationCursor(page.nextCursor);
            setHasMoreCards(Boolean(page.nextCursor));
            return;
          } catch (cardError) {
            if (!isMounted) {
              return;
            }

            console.error("Unable to load fallback MCAAD wall cards", cardError);
          }
        }

        setLiveCards([]);
        setCardsLoadFailed(true);
      } finally {
        await partnerBrandingPromise;
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadWall();

    return () => {
      isMounted = false;
    };
  }, [applyWallCardPage, wallId]);

  useEffect(() => {
    if (!wall || wallUnavailable || wall.id !== wallId) {
      return;
    }

    const visitKey = `${location.key || location.pathname}:${wallId}`;
    if (trackedWallVisitRef.current === visitKey) {
      return;
    }

    trackedWallVisitRef.current = visitKey;
    trackMixpanelEvent("Experience Viewed", {
      experience: "partner_wall",
      entry_point: "partner_wall",
      wall_id: wallId,
      partner_id: getWallPartnerId(wall),
      user_state: getAnalyticsUserState(activeUser),
      language: navigator.language,
    });
  }, [activeUser, location.key, location.pathname, wall, wallId, wallUnavailable]);

  const loadMoreCards = useCallback(async ({ retry = false } = {}) => {
    if (
      !hasMoreCards ||
      !paginationCursor ||
      isLoadingMore ||
      (paginationLoadFailed && !retry)
    ) {
      return;
    }

    const requestKey = `${wallId}:${paginationCursor}`;
    if (paginationRequestKeyRef.current === requestKey) {
      return;
    }

    const requestSessionId = wallSessionRef.current;
    paginationRequestKeyRef.current = requestKey;
    setIsLoadingMore(true);
    setPaginationLoadFailed(false);

    try {
      const page = applyWallCardPage(
        await getPublicWallCards(
          wallId,
          WALL_CARD_PAGE_SIZE,
          paginationCursor
        ),
        wallId
      );

      if (
        wallSessionRef.current !== requestSessionId ||
        !page
      ) {
        return;
      }

      setLiveCards((previousCards) => {
        const seenCardIds = new Set(
          [...seedCards, ...previousCards].map((card) => card.id).filter(Boolean)
        );
        const newCards = page.cards.filter((card) => {
          if (!card.id) {
            return true;
          }

          if (seenCardIds.has(card.id)) {
            return false;
          }

          seenCardIds.add(card.id);
          return true;
        });

        return [...previousCards, ...newCards];
      });
      setPaginationCursor(page.nextCursor);
      setHasMoreCards(Boolean(page.nextCursor));
      setPaginationLoadFailed(false);
    } catch (error) {
      if (wallSessionRef.current !== requestSessionId) {
        return;
      }

      if (
        paginationCursor &&
        isInvalidWallCursorError(error) &&
        !cursorRecoveryAttemptedRef.current
      ) {
        cursorRecoveryAttemptedRef.current = true;

        try {
          const firstPage = applyWallCardPage(
            await getPublicWallCards(wallId, WALL_CARD_PAGE_SIZE),
            wallId
          );

          if (wallSessionRef.current !== requestSessionId) {
            return;
          }

          setSeedCards(firstPage.seedCards);
          setLiveCards(firstPage.cards);
          setPaginationCursor(firstPage.nextCursor);
          setHasMoreCards(Boolean(firstPage.nextCursor));
          setPaginationLoadFailed(false);
          arrivalAdditionalPagesRef.current = 0;
          return;
        } catch (reloadError) {
          if (wallSessionRef.current !== requestSessionId) {
            return;
          }

          console.error("Unable to reload public wall after cursor rejection", reloadError);
          setPaginationLoadFailed(true);
          return;
        }
      }

      console.error("Unable to load more public wall cards", error);
      setPaginationLoadFailed(true);
    } finally {
      if (wallSessionRef.current === requestSessionId) {
        paginationRequestKeyRef.current = null;
        setIsLoadingMore(false);
      }
    }
  }, [
    applyWallCardPage,
    hasMoreCards,
    isLoadingMore,
    paginationCursor,
    paginationLoadFailed,
    seedCards,
    wallId,
  ]);

  const retryLoadMoreCards = useCallback(() => {
    setPaginationLoadFailed(false);
    loadMoreCards({ retry: true });
  }, [loadMoreCards]);

  useEffect(() => {
    if (!arrival || arrival.wallId !== wallId || arrivalFallback) {
      return;
    }

    const targetLoaded = liveCards.some((card) => card.id === arrival.cardId) ||
      seedCards.some((card) => card.id === arrival.cardId);
    if (targetLoaded) {
      return;
    }

    if (!isLoading && (cardsLoadFailed || wallUnavailable || paginationLoadFailed)) {
      setArrivalFallback(true);
      return;
    }

    if (isLoading || isLoadingMore) {
      return;
    }

    if (hasMoreCards && arrivalAdditionalPagesRef.current < 2) {
      arrivalAdditionalPagesRef.current += 1;
      loadMoreCards();
      return;
    }

    if (!hasMoreCards || arrivalAdditionalPagesRef.current >= 2) {
      setArrivalFallback(true);
    }
  }, [
    arrival,
    arrivalFallback,
    cardsLoadFailed,
    hasMoreCards,
    isLoading,
    isLoadingMore,
    liveCards,
    loadMoreCards,
    paginationLoadFailed,
    seedCards,
    wallId,
    wallUnavailable,
  ]);

  const seedCardIds = useMemo(
    () => new Set(seedCards.map((card) => card.id).filter(Boolean)),
    [seedCards]
  );
  const communityCardIds = useMemo(
    () => new Set(
      liveCards
        .filter((card) => card.id && !seedCardIds.has(card.id))
        .map((card) => card.id)
    ),
    [liveCards, seedCardIds]
  );
  const wallCards = useMemo(
    () => {
      const dedupedLiveCards = liveCards.filter((card) => !seedCardIds.has(card.id));
      return buildWallDisplayCards({
        liveCards: [...seedCards, ...dedupedLiveCards],
        fallbackCards: SHOW_FALLBACK_WALL_CARDS ? undefined : [],
        maxCards: undefined,
      });
    },
    [liveCards, seedCardIds, seedCards]
  );
  const arrivalCardId =
    arrival && arrival.wallId === wallId &&
    wallCards.some((card) => card.id === arrival.cardId)
      ? arrival.cardId
      : "";
  const createCardPath = useMemo(() => getCreateCardPath(wall), [wall]);
  const emptyWallMessage = wallUnavailable || cardsLoadFailed
    ? "This wall isn't available right now."
    : "The first conversations will appear here.";

  const wallBrandHeader = useMemo(() => wall ? (
      <EmpathyPageHeader
        title={wall?.eyebrow || "Community Wall"}
        partnerBranding={wall?.partnerBranding}
        authUser={activeUser}
        showBackButton={false}
        className="font-sans"
      />
  ) : null, [activeUser, wall]);

  const wallHeader = useMemo(() => (
    <>
      {wallBrandHeader}
      <div className="mt-2 px-5 pt-4 pb-1 sm:mt-3 sm:px-8 sm:pt-6 sm:pb-2">
        <h1 className="mx-auto max-w-2xl text-4xl font-medium leading-[0.95] tracking-[-0.04em] text-white sm:text-6xl">
          {wallUnavailable
            ? "This wall isn't available."
            : wall?.title || "Community Wall"}
        </h1>

        <p className="mx-auto mt-3 max-w-xl font-sans text-sm leading-relaxed text-white sm:text-base">
          {wallUnavailable
            ? "It may be private, inactive, or the link may no longer work."
            : WALL_HELPER_COPY}
        </p>
      </div>
    </>
  ), [wallBrandHeader, wall, wallUnavailable]);

  return (
    <div
      className="min-h-screen min-h-[100dvh] overflow-x-hidden font-serif text-gray-950"
      style={{ backgroundColor: REFINE_DARK_BACKGROUND }}
    >
      <Helmet>
        <meta
          name="theme-color"
          content={REFINE_DARK_BACKGROUND}
        />

        <meta
          name="apple-mobile-web-app-status-bar-style"
          content="black"
        />
      </Helmet>

      <main className="relative h-screen h-[100dvh] overflow-hidden">
        <section className="relative min-h-screen overflow-hidden">
          <div className="pointer-events-none absolute inset-x-[-12%] bottom-[-18vh] z-0 h-[54vh] rounded-[50%] bg-[radial-gradient(ellipse_at_center,rgba(244,217,78,0.42)_0%,rgba(106,174,37,0.22)_34%,rgba(255,255,255,0.1)_54%,transparent_74%)] blur-3xl" />

          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 mx-auto h-[68vh] max-w-3xl overflow-visible">
            <div className="relative h-full">
              <BackgroundLights />
            </div>
          </div>

          <WallCardInteractionLayer
            cards={wallCards}
            header={wallHeader}
            wallTitle={wall?.title || "Community Wall"}
            emptyMessage={emptyWallMessage}
            createCardPath={createCardPath}
            isLoading={isLoading}
            hasMore={hasMoreCards}
            isLoadingMore={isLoadingMore}
            loadMoreError={paginationLoadFailed}
            onLoadMore={loadMoreCards}
            onRetryLoadMore={retryLoadMoreCards}
            arrivalCardId={arrivalCardId}
            highlightedCardId={highlightedCardId}
            onArrivalTargetRendered={handleArrivalTargetRendered}
            partnerId={getWallPartnerId(wall)}
            seedCardIds={seedCardIds}
            communityCardIds={communityCardIds}
            wallId={wallId}
            scrollResetKey={location.key}
          />
        </section>
      </main>

      {createCardPath && !isLoading ? (
        <div className="fixed bottom-5 right-5 z-40 sm:bottom-6 sm:right-6">
          <ActionButton
            href={createCardPath}
            onClick={() => trackEvent("CTA", "Add My Card Wall Floating", "Wall")}
            className="shadow-[0_18px_48px_rgba(0,0,0,0.34)]"
            icon="plus"
          >
            Create Card
          </ActionButton>
        </div>
      ) : null}

      {arrivalFallback && arrival ? (
        <div
          className="fixed inset-x-4 top-4 z-50 mx-auto max-w-lg rounded-2xl border border-white/15 bg-[#151710]/95 px-5 py-4 text-center text-white shadow-[0_16px_48px_rgba(0,0,0,0.45)] backdrop-blur-xl"
          role="status"
          aria-live="polite"
        >
          <p className="font-sans text-sm font-semibold">Your card was added</p>
          <p className="mt-1 font-sans text-sm text-white/75">
            We couldn’t load the wall right now.
          </p>
          <a
            className="mt-2 inline-flex rounded-full px-3 py-1 font-sans text-sm font-semibold text-[#f4d94e] underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f4d94e]/70"
            href={`/card/${encodeURIComponent(arrival.cardId)}`}
          >
            View card
          </a>
        </div>
      ) : arrivalToastVisible ? (
        <div
          className="fixed inset-x-4 top-4 z-50 mx-auto w-fit rounded-full border border-white/15 bg-[#151710]/95 px-5 py-3 font-sans text-sm font-semibold text-white shadow-[0_16px_48px_rgba(0,0,0,0.45)] backdrop-blur-xl"
          role="status"
          aria-live="polite"
        >
          ✓ Added to the wall
        </div>
      ) : arrivalSlow && arrival ? (
        <div
          className="fixed inset-x-4 top-4 z-50 mx-auto w-fit rounded-full border border-white/10 bg-[#151710]/90 px-5 py-3 font-sans text-sm text-white/80 shadow-lg"
          role="status"
          aria-live="polite"
        >
          Your card was added. Finding it on the wall...
        </div>
      ) : null}

    </div>
  );
};

export default WallView;
